/**
 * 워크스페이스 역할로 초대·플랜 만들기·감사를 가른다 (PRD2 §6.1).
 * 로컬 기본은 소유자다. 참여자 보기는 뷰어다.
 */

import { useSyncExternalStore } from "react";
import { useCatalogStore } from "@/store/catalog-store";
import { useProjectStore } from "@/store/project-store";
import { auditEvent, type AuditSeverity } from "./audit-catalog";
import { workspaceGrant, type WorkspaceRole } from "./matrix";

export type { WorkspaceRole };

export function useWorkspaceRole(): WorkspaceRole {
  const guest = useProjectStore((s) => s.previewGuest);
  const admin = useProjectStore((s) => s.previewWorkspaceAdmin);
  const member = useProjectStore((s) => s.previewWorkspaceMember);
  if (guest) return "guest";
  if (member) return "viewer";
  if (admin) return "admin";
  return "owner";
}

/** 미리보기가 꺼져 있으면 그 워크스페이스에 저장된 역할을 쓴다. */
export function useEffectiveWorkspaceRole(): WorkspaceRole {
  const stored = useCatalogStore((s) => s.workspaces[s.activeWorkspaceId]?.role);
  const preview = useWorkspaceRole();
  const guest = useProjectStore((s) => s.previewGuest);
  const admin = useProjectStore((s) => s.previewWorkspaceAdmin);
  const member = useProjectStore((s) => s.previewWorkspaceMember);
  if (guest || admin || member) return preview;
  if (stored === "admin" || stored === "editor" || stored === "viewer" || stored === "owner") return stored;
  return "owner";
}

export function canWorkspace(role: WorkspaceRole, actionId: string): boolean {
  return workspaceGrant(actionId, role).result === "allow";
}

export type WorkspaceMemberRole = "owner" | "admin" | "editor" | "viewer";

export type WorkspaceMember = {
  id: string;
  name: string;
  email: string;
  role: WorkspaceMemberRole;
};

const SEED: WorkspaceMember[] = [
  { id: "me", name: "나", email: "me@wavex.local", role: "owner" },
  { id: "pm", name: "김PM", email: "pm@wavex.local", role: "admin" },
  { id: "lee", name: "이편집", email: "lee@wavex.local", role: "editor" },
];

let members: WorkspaceMember[] = SEED.map((row) => ({ ...row }));
const listeners = new Set<() => void>();

function emit() {
  members = members.map((row) => ({ ...row }));
  for (const listener of listeners) listener();
}

export function resetWorkspaceMembers(): void {
  members = SEED.map((row) => ({ ...row }));
  emit();
}

export function getWorkspaceMembers(): WorkspaceMember[] {
  return members;
}

export function subscribeWorkspaceMembers(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useWorkspaceMembers(): WorkspaceMember[] {
  return useSyncExternalStore(subscribeWorkspaceMembers, getWorkspaceMembers, getWorkspaceMembers);
}

export function useWorkspaceAuditRows(): WorkspaceAuditRow[] {
  return useSyncExternalStore(subscribeWorkspaceAudit, workspaceAuditRows, workspaceAuditRows);
}

/** 소유자 행은 바꾸지 않는다. */
export function setWorkspaceMemberRole(id: string, role: WorkspaceMemberRole): boolean {
  const row = members.find((item) => item.id === id);
  if (!row || row.role === "owner" || role === "owner") return false;
  row.role = role;
  emit();
  return true;
}

export const WORKSPACE_ROLE_LABEL: Record<WorkspaceMemberRole, string> = {
  owner: "소유자",
  admin: "관리자",
  editor: "편집자",
  viewer: "뷰어",
};

export type WorkspaceAuditRow = {
  id: string;
  when: string;
  who: string;
  label: string;
  /** 무엇 칸. 없으면 이벤트 라벨만 보인다. */
  detail: string;
  severity: AuditSeverity;
};

const AUDIT_SEED = [
  { id: "w1", when: "2026-10-01T09:00:00", who: "나", eventId: "auth.signed_in", detail: "이 워크스페이스에 로그인했습니다." },
  { id: "w2", when: "2026-09-28T11:20:00", who: "나", eventId: "member.invited", detail: "멤버 초대를 보냈습니다." },
  { id: "w3", when: "2026-09-20T16:05:00", who: "김PM", eventId: "plan.created", detail: "플랜을 만들었습니다." },
];

type LiveAudit = { id: string; when: string; who: string; label: string; detail: string; severity: AuditSeverity };

let liveAudits: LiveAudit[] = [];
let liveAuditSeq = 0;
const auditListeners = new Set<() => void>();

function seedAuditRows(): WorkspaceAuditRow[] {
  return AUDIT_SEED.map((row) => ({
    id: row.id,
    when: row.when,
    who: row.who,
    label: auditEvent(row.eventId)?.label ?? "",
    detail: row.detail,
    severity: auditEvent(row.eventId)?.severity ?? "info",
  }));
}

let auditSnapshot: WorkspaceAuditRow[] = seedAuditRows();

function emitAudit() {
  liveAudits = liveAudits.map((row) => ({ ...row }));
  auditSnapshot = [...liveAudits, ...seedAuditRows()];
  for (const listener of auditListeners) listener();
}

/** 결정만 앞에 쌓는다. 남은 일수가 하루 줄어드는 것은 기록하지 않는다. */
export function recordWorkspaceAudit(label: string, detail: string, who = "나", severity: AuditSeverity = "info"): void {
  liveAuditSeq += 1;
  liveAudits = [
    { id: `w-live-${liveAuditSeq}`, when: new Date().toISOString(), who, label, detail, severity },
    ...liveAudits,
  ];
  emitAudit();
}

export function resetWorkspaceAudit(): void {
  liveAudits = [];
  emitAudit();
}

export function subscribeWorkspaceAudit(listener: () => void): () => void {
  auditListeners.add(listener);
  return () => auditListeners.delete(listener);
}

export function workspaceAuditRows(): WorkspaceAuditRow[] {
  return auditSnapshot;
}
